import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import {
  FileErrorDialogType,
  FileErrorModalComponent,
} from '../../modal-dialog/file-error-modal/file-error-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FileExportModalComponent } from '../../modal-dialog/file-export-modal/file-export-modal.component';
import { InventoryTableSearchComponent } from './inventory-table-search/inventory-table-search.component';
import { InventoryItemModalComponent } from '../../modal-dialog/inventory-item-modal/inventory-item-modal.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { InventoryFileService } from '@app/services/inventory/inventory-file.service';
import { IconName } from '@app/utils/enums/icon-enum';
import { FileUploadErrors } from '@app/utils/constants/file-upload-error.constants';
import { REGEX } from '@app/utils/constants/regex.constants';
import { protectLongNumbers } from '@app/utils/helpers/protect-long-numbers/protect-long-numbers.helper';
import { InventoryQueryParams } from '@app/utils/interfaces/inventory-item.interface';

@Component({
  selector: 'app-inventory-table-functions',
  standalone: true,
  imports: [IconComponent, TranslateModule, InventoryTableSearchComponent],
  templateUrl: './inventory-table-functions.component.html',
  styleUrl: './inventory-table-functions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryTableFunctionsComponent {
  private readonly inventoryFileService = inject(InventoryFileService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  public readonly activeFilters = input<InventoryQueryParams>({});
  public readonly hasActiveFilters = input(true);

  public readonly searchEvent = output<string>();
  public readonly openFilterDrawer = output<void>();

  public readonly filtersReseted = output<void>();
  public readonly filterRemoved = output<keyof InventoryQueryParams>();

  public readonly iconName = IconName;

  private readonly activeExportDialog = signal<MatDialogRef<FileExportModalComponent> | null>(null);

  public readonly activeFilterKeys = computed(() => {
  const filters = this.activeFilters();
  
  const excludedKeys = new Set(['pageNumber', 'pageSize', 'sort']); 

  return Object.keys(filters).filter(key => {
    const value = filters[key];
    
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      !excludedKeys.has(key)
    );
  });
});

  public onSearchInput(term: string): void {
    this.searchEvent.emit(term);
  }

  public openFilter(): void {
    this.openFilterDrawer.emit();
  }

  public onExport(): void {
    const dialogRef = this.showProcessing();
    this.activeExportDialog.set(dialogRef);

    this.inventoryFileService
      .exportFile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => void this.handleExportSuccess(blob, dialogRef),
        error: () => this.handleExportError(dialogRef),
      });
  }

  public openAddInventoryItem(): void {
    this.dialog.open(InventoryItemModalComponent, {
      data: { mode: 'create' },
    });
  }

  private async handleExportSuccess(
    blob: Blob,
    dialogRef: MatDialogRef<FileExportModalComponent>,
  ): Promise<void> {
    try {
      let csvText = await blob.text();
      csvText = protectLongNumbers(csvText);
      csvText = csvText.replace(REGEX.differentLocalesColon, ';');
      const BOM = '\uFEFF';
      const file = new Blob([BOM + csvText], { type: 'text/csv;charset=utf-8;' });

      saveAs(file);
      dialogRef.close();
      this.activeExportDialog.set(null);
    } catch {
      this.handleExportError(dialogRef);
    }
  }

  private handleExportError(dialogRef: MatDialogRef<FileExportModalComponent>): void {
    dialogRef.close();
    this.activeExportDialog.set(null);

    this.dialog.open(FileErrorModalComponent, {
      data: {
        title: FileUploadErrors.EXPORT_FAILED.title,
        subtitle: FileUploadErrors.EXPORT_FAILED.subtitle,
        message: FileUploadErrors.EXPORT_FAILED.message,
        type: FileErrorDialogType.Export,
        actionButtonCallback: () => this.onExport(),
      },
    });
  }

  private showProcessing(): MatDialogRef<FileExportModalComponent> {
    return this.dialog.open(FileExportModalComponent, {
      disableClose: false,
    });
  }

  public removeFilter(key: keyof InventoryQueryParams): void {
    this.filterRemoved.emit(key);
  }

  public onResetAll(): void {
    this.filtersReseted.emit();
  }
}
